// -----------------------------------------------------------------------------------------------------

// The file is a utility file for the overlay functionality. It is responsible for doing the following:

// • Handle the opening and closing of a modal / popup / overlay / drawer
// • Manage body overlay visibility
// • Provide specific cart drawer open/close functionality
// • Bind event listeners for overlay interactions

// -----------------------------------------------------------------------------------------------------

window.themeUtils = window.themeUtils || {};

const bodyOverlay = document.querySelector('.body__overlay');
const bodyActiveClass = 'body__overlay--visible';

// -----------------------------------------------------------------------------------------------------
// • Helper: Pause all videos in a container
// -----------------------------------------------------------------------------------------------------

/**
 * Pause all videos inside a given container element
 * @param {HTMLElement} container
 */
window.themeUtils.pauseVideos = function (container) {
  if (!container) return;
  const videos = container.querySelectorAll('video');
  videos.forEach(video => {
    video.pause();
  });
};

// -----------------------------------------------------------------------------------------------------
// • Open an overlay
// -----------------------------------------------------------------------------------------------------

/**
 * Open an overlay
 * @param {string} targetClass - Class of the element to open
 * @param {string} activeClass - The active class of the element when open
 * @param {number} delay - Delay before adding active class (in ms)
 */
window.themeUtils.openOverlay = function (targetClass, activeClass, delay = 0) {
  const targetElement = document.querySelector(targetClass);
  if (!targetElement) {
    console.error('Target element not found:', targetClass);
    return;
  }

  targetElement.classList.add(`${activeClass.split('--')[0]}--transitioning`);

  if (delay > 0) {
    setTimeout(() => {
      targetElement.classList.add(activeClass);
    }, delay);
  } else {
    targetElement.classList.add(activeClass);
  }
};

// -----------------------------------------------------------------------------------------------------
// • Close an overlay
// -----------------------------------------------------------------------------------------------------

/**
 * Close an overlay
 * @param {string} targetClass - Class of the element to close
 * @param {string} activeClass - The active class of the element when open
 * @param {number} delay - Delay before removing active class (in ms)
 */
window.themeUtils.closeOverlay = function (targetClass, activeClass, delay = 0) {
  const targetElement = document.querySelector(targetClass);
  if (!targetElement) {
    console.error('Target element not found:', targetClass);
    return;
  }

  targetElement.classList.add(`${activeClass.split('--')[0]}--transitioning`);

  const removeClasses = () => {
    targetElement.classList.remove(activeClass);
    targetElement.classList.remove(`${activeClass.split('--')[0]}--transitioning`);

    // Pause all videos in this overlay
    window.themeUtils.pauseVideos(targetElement);
  };

  if (delay > 0) {
    setTimeout(removeClasses, delay);
  } else {
    removeClasses();
  }
};

// -----------------------------------------------------------------------------------------------------
// • Open a modal by ID
// -----------------------------------------------------------------------------------------------------

/**
 * Open a modal by its ID
 * @param {string} modalId - The ID of the modal to open
 */
window.themeUtils.openModal = function (modalId) {
  const modal = document.querySelector(`[data-modal][data-modal-id="${modalId}"]`);
  if (!modal) {
    console.error('Modal not found:', modalId);
    return;
  }

  if (!bodyOverlay) {
    console.error('Body overlay not found');
    return;
  }

  bodyOverlay.classList.add(bodyActiveClass);

  // Dispatch modal open event
  const modalOpenEvent = new CustomEvent('modal:open', {
    detail: { modalId: modalId }
  });
  document.dispatchEvent(modalOpenEvent);

  const baseClass = Array.from(modal.classList).find(cls => !cls.includes('--'));

  setTimeout(() => {
    if (baseClass) {
      modal.classList.add(`${baseClass}--transitioning`);
      modal.classList.add(`${baseClass}--active`);

      setTimeout(() => {
        modal.classList.remove(`${baseClass}--transitioning`);
      }, 300);
    }
  }, 50);
};

// -----------------------------------------------------------------------------------------------------
// • Close a modal by ID
// -----------------------------------------------------------------------------------------------------

/**
 * Close a modal by its ID
 * @param {string} modalId - The ID of the modal to close
 */
window.themeUtils.closeModal = function (modalId) {
  const modal = document.querySelector(`[data-modal][data-modal-id="${modalId}"]`);
  if (!modal) {
    console.error('Modal not found:', modalId);
    return;
  }

  if (!bodyOverlay) {
    console.error('Body overlay not found');
    return;
  }

  const baseClass = Array.from(modal.classList).find(cls => !cls.includes('--'));

  if (baseClass) {
    modal.classList.add(`${baseClass}--transitioning`);
    modal.classList.remove(`${baseClass}--active`);
  }

  // Pause all videos in this modal
  window.themeUtils.pauseVideos(modal);

  // Dispatch modal close event
  const modalCloseEvent = new CustomEvent('modal:close', {
    detail: { modalId: modalId }
  });
  document.dispatchEvent(modalCloseEvent);

  setTimeout(() => {
    bodyOverlay.classList.remove(bodyActiveClass);
    if (baseClass) {
      modal.classList.remove(`${baseClass}--transitioning`);
    }
  }, 300);
};

// -----------------------------------------------------------------------------------------------------
// • Open the cart drawer
// -----------------------------------------------------------------------------------------------------

window.themeUtils.openCartDrawer = function () {
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  if (!cartDrawer) {
    console.error('Cart drawer not found');
    return;
  }

  if (!bodyOverlay) {
    console.error('Body overlay not found');
    return;
  }

  cartDrawer.classList.remove('cart-drawer--closing');

  bodyOverlay.classList.add(bodyActiveClass);

  setTimeout(() => {
    cartDrawer.classList.add('cart-drawer--transitioning');
    cartDrawer.classList.add('cart-drawer--active');

    if (window.innerWidth >= 1024) {
      cartDrawer.classList.add('cart-drawer--sidebar-active');
    }

    setTimeout(() => {
      cartDrawer.classList.remove('cart-drawer--transitioning');
    }, 280);
  }, 150);
};

// -----------------------------------------------------------------------------------------------------
// • Close the cart drawer
// -----------------------------------------------------------------------------------------------------

window.themeUtils.closeCartDrawer = function () {
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  if (!cartDrawer) {
    console.error('Cart drawer not found');
    return;
  }

  if (!bodyOverlay) {
    console.error('Body overlay not found');
    return;
  }

  cartDrawer.classList.add('cart-drawer--transitioning');
  cartDrawer.classList.add('cart-drawer--closing');
  cartDrawer.classList.remove('cart-drawer--active');
  cartDrawer.classList.remove('cart-drawer--sidebar-active');

  // Pause all videos in cart drawer (if any)
  window.themeUtils.pauseVideos(cartDrawer);

  setTimeout(() => {
    bodyOverlay.classList.remove(bodyActiveClass);
    cartDrawer.classList.remove('cart-drawer--transitioning');
    cartDrawer.classList.remove('cart-drawer--closing');
  }, 150);
};

// -----------------------------------------------------------------------------------------------------
// • Open the cart drawer sidebar (mobile only)
// -----------------------------------------------------------------------------------------------------

window.themeUtils.openCartDrawerSidebar = function () {
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  if (!cartDrawer) return;

  cartDrawer.classList.add('cart-drawer--transitioning');
  cartDrawer.classList.add('cart-drawer--sidebar-active');

  setTimeout(() => {
    cartDrawer.classList.remove('cart-drawer--transitioning');
  }, 280);
};

// -----------------------------------------------------------------------------------------------------
// • Close the cart drawer sidebar (mobile only)
// -----------------------------------------------------------------------------------------------------

window.themeUtils.closeCartDrawerSidebar = function () {
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  if (!cartDrawer) return;

  cartDrawer.classList.add('cart-drawer--transitioning');
  cartDrawer.classList.remove('cart-drawer--sidebar-active');

  // Pause all videos in sidebar (if any)
  window.themeUtils.pauseVideos(cartDrawer);

  setTimeout(() => {
    cartDrawer.classList.remove('cart-drawer--transitioning');
  }, 280);
};

// -----------------------------------------------------------------------------------------------------
// • Bind overlay event listeners
// -----------------------------------------------------------------------------------------------------

window.themeUtils.bindOverlayEventListeners = function () {
  if (!window.themeUtils.overlayEventListenersBound) {
    const cartDrawerTrigger = document.querySelector('[data-cart-drawer-trigger]');

    if (cartDrawerTrigger) {
      cartDrawerTrigger.addEventListener('click', function () {
        window.themeUtils.openCartDrawer();
      });
    }

    document.addEventListener('click', function (event) {
      if (event.target.matches('[data-close-cart-drawer]') || event.target.closest('[data-close-cart-drawer]')) {
        window.themeUtils.closeCartDrawer();
      }
    });

    if (bodyOverlay) {
      bodyOverlay.addEventListener('click', function () {
        const cartDrawer = document.querySelector('[data-cart-drawer].cart-drawer--active');
        if (cartDrawer) {
          window.themeUtils.closeCartDrawer();
          return;
        }

        const activeModal = document.querySelector('[data-modal][class*="--active"]');
        if (activeModal) {
          const modalId = activeModal.getAttribute('data-modal-id');
          if (modalId) {
            window.themeUtils.closeModal(modalId);
          }
        }
      });
    }

    document.addEventListener('click', function (event) {
      if (event.target.matches('[data-cart-show-upsells]')) {
        if (window.innerWidth < 1024) {
          window.themeUtils.openCartDrawerSidebar();
        }
      }

      if (event.target.matches('[data-cart-sidebar-back]')) {
        if (window.innerWidth < 1024) {
          window.themeUtils.closeCartDrawerSidebar();
        }
      }
    });

    document.addEventListener('click', function (event) {
      const trigger = event.target.closest('[data-modal-trigger]');
      if (trigger) {
        const modalId = trigger.getAttribute('data-modal-trigger-id');
        if (modalId) {
          window.themeUtils.openModal(modalId);
        }
      }
    });

    document.addEventListener('click', function (event) {
      const closeButton = event.target.closest('[data-modal-close]');
      if (closeButton) {
        const modalId = closeButton.getAttribute('data-modal-close-id');
        if (modalId) {
          window.themeUtils.closeModal(modalId);
        }
      }
    });

    window.themeUtils.overlayEventListenersBound = true;
  }
};

// -----------------------------------------------------------------------------------------------------
// • Initial binding of overlay event listeners when DOM is ready
// -----------------------------------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', function () {
  window.themeUtils.bindOverlayEventListeners();
});